// wsSimulator: drives mock campaigns forward on a timer. Moves queued points
// to running, running to done, then after a full batch completes emits a
// metrics rollup and asks the mock sampler for the next batch.

import type {
  CampaignState,
  SamplePoint,
  WebSocketEvent,
} from "../types";
import { campaignStore } from "./campaignStore";
import { lookup as oracleLookup } from "./oracleLoader";
import {
  fitAllOutputs,
  makeRng,
  normalisePoint,
  proposeBatch,
  rangeAxes,
  type PerOutputModel,
} from "./_mock_sampler";
import { predict } from "./surrogate";

const TICK_MS = 500; // 2 Hz

type PointSimState = {
  startMs: number;
  targetDurationSec: number;
};

// Per-campaign runtime state held outside CampaignState to avoid persisting
// internal simulator bookkeeping.
type CampaignRuntime = {
  pointSim: Map<string, PointSimState>; // pointId -> sim state
  nextBatchArmed: boolean;
  seedPart: number;
};

const RUNTIMES = new Map<string, CampaignRuntime>();

function getOrCreateRuntime(id: string): CampaignRuntime {
  let rt = RUNTIMES.get(id);
  if (!rt) {
    rt = { pointSim: new Map(), nextBatchArmed: false, seedPart: 0 };
    RUNTIMES.set(id, rt);
  }
  return rt;
}

let tickTimer: ReturnType<typeof setInterval> | null = null;

export function ensureTickLoopRunning(): void {
  if (tickTimer) return;
  tickTimer = setInterval(tickAll, TICK_MS);
}

export function stopTickLoop(): void {
  if (tickTimer) {
    clearInterval(tickTimer);
    tickTimer = null;
  }
}

function now(): number {
  return Date.now();
}

// Seeded random number generator for deterministic runtime/cost jitter.
function makeRuntimeRng(campaignId: string, pointId: string): () => number {
  return makeRng(campaignId, `runtime-${pointId}`);
}

function simulateRuntimeSec(campaignId: string, pointId: string): number {
  const rng = makeRuntimeRng(campaignId, pointId);
  return 10 + 30 * rng();
}

function truthForPoint(
  state: CampaignState,
  point: SamplePoint,
): Record<string, number> | null {
  const oracle = state.config.oracle;
  if (!oracle?.enabled || !oracle.datasetId) return null;
  const res = oracleLookup(oracle.datasetId, point.inputs);
  if (!res.ok) return null;
  // Narrow to only outputs the campaign cares about.
  const filtered: Record<string, number> = {};
  for (const o of state.config.outputs) {
    const v = res.outputs[o.name];
    if (v != null) filtered[o.name] = v;
  }
  return filtered;
}

function syntheticOutputs(
  state: CampaignState,
  point: SamplePoint,
  models: PerOutputModel[],
  rng: () => number,
): Record<string, number> {
  const range = rangeAxes(state.config.inputs);
  const xn = normalisePoint(point.inputs, range);
  const out: Record<string, number> = {};
  for (const { name, model } of models) {
    const { mean, sigma } = predict(model, xn);
    // Fallback when no data: small signal from input magnitude.
    const base = Number.isFinite(mean) ? mean : 0;
    const noise = (rng() - 0.5) * 2 * Math.max(sigma, 0.05);
    out[name] = base + noise;
  }
  return out;
}

function completedObservations(state: CampaignState): SamplePoint[] {
  return state.points.filter(
    (p) => p.status === "done" && p.outputs != null,
  );
}

function currentBatchComplete(state: CampaignState): boolean {
  const activeBatchId = state.points
    .filter((p) => p.iter === state.iteration)
    .map((p) => p.batchId)[0];
  if (!activeBatchId) return false;
  const batch = state.points.filter((p) => p.batchId === activeBatchId);
  if (batch.length === 0) return false;
  return batch.every((p) => p.status === "done" || p.status === "failed");
}

function targetsMet(state: CampaignState): boolean {
  if (!state.config.stopEarlyIfTargetsMet) return false;
  let anyChecked = false;
  for (const o of state.config.outputs) {
    const m = state.metrics.perOutput[o.name];
    if (!m || o.targetSigma == null) continue;
    anyChecked = true;
    if (m.currentMaxSigma > o.targetSigma) return false;
  }
  return anyChecked;
}

// ---------------------------------------------------------------------------
// Metrics recomputation
// ---------------------------------------------------------------------------

function recomputeMetrics(state: CampaignState): void {
  const observed = completedObservations(state);
  const models = fitAllOutputs(state.config.inputs, state.config.outputs, observed);
  const range = rangeAxes(state.config.inputs);

  // Sample sigma at the current observed points to get "currentMax/Mean".
  for (const o of state.config.outputs) {
    const m = state.metrics.perOutput[o.name];
    if (!m) continue;
    const model = models.find((x) => x.name === o.name)?.model;
    if (!model || observed.length === 0) continue;
    let maxSig = 0;
    let sumSig = 0;
    for (const p of observed) {
      const xn = normalisePoint(p.inputs, range);
      const { sigma } = predict(model, xn);
      if (sigma > maxSig) maxSig = sigma;
      sumSig += sigma;
    }
    m.currentMaxSigma = maxSig;
    m.currentMeanSigma = observed.length > 0 ? sumSig / observed.length : 0;
  }

  // Oracle-mode realised RMSE on a 50x50 grid across the first two range axes.
  if (state.config.oracle?.enabled && state.config.oracle.datasetId && range.length >= 2) {
    const ax1 = range[0];
    const ax2 = range[1];
    if (!ax1 || !ax2) return;
    const datasetId = state.config.oracle.datasetId;
    const res = 50;
    const xs: number[] = [];
    const ys: number[] = [];
    const xMin = ax1.min ?? 0;
    const xMax = ax1.max ?? 1;
    const yMin = ax2.min ?? 0;
    const yMax = ax2.max ?? 1;
    for (let i = 0; i < res; i++) xs.push(xMin + ((xMax - xMin) * i) / (res - 1));
    for (let j = 0; j < res; j++) ys.push(yMin + ((yMax - yMin) * j) / (res - 1));

    for (const o of state.config.outputs) {
      const m = state.metrics.perOutput[o.name];
      if (!m || !m.realRmseByIter) continue;
      const model = models.find((x) => x.name === o.name)?.model;
      if (!model) continue;

      let sse = 0;
      let cnt = 0;
      for (const xv of xs) {
        for (const yv of ys) {
          const inputs: Record<string, number> = { [ax1.name]: xv, [ax2.name]: yv };
          // Pin other axes to midpoint.
          for (const a of range.slice(2)) {
            inputs[a.name] = ((a.min ?? 0) + (a.max ?? 1)) / 2;
          }
          for (const a of state.config.inputs) {
            if (a.mode === "fixed" && a.fixedValue != null) {
              inputs[a.name] = a.fixedValue;
            }
          }
          const truth = oracleLookup(datasetId, inputs);
          if (!truth.ok) continue;
          const t = truth.outputs[o.name];
          if (t == null) continue;
          const xn = normalisePoint(inputs, range);
          const { mean } = predict(model, xn);
          const err = mean - t;
          sse += err * err;
          cnt += 1;
        }
      }
      if (cnt > 0) {
        const rmse = Math.sqrt(sse / cnt);
        m.currentRealRmse = rmse;
      }
    }
  }
}

function pushBatchMetrics(state: CampaignState): void {
  for (const o of state.config.outputs) {
    const m = state.metrics.perOutput[o.name];
    if (!m) continue;
    m.maxSigmaByIter.push(m.currentMaxSigma);
    m.meanSigmaByIter.push(m.currentMeanSigma);
    if (m.realRmseByIter && m.currentRealRmse != null) {
      m.realRmseByIter.push(m.currentRealRmse);
    }
  }
}

function recordCalibration(state: CampaignState, point: SamplePoint): void {
  if (!state.config.oracle?.enabled) return;
  if (!point.outputs || !point.predicted || !point.truthOutputs) return;
  for (const o of state.config.outputs) {
    const m = state.metrics.perOutput[o.name];
    if (!m) continue;
    const predicted = point.predicted[o.name];
    const truth = point.truthOutputs[o.name];
    if (predicted == null || truth == null) continue;
    if (!m.calibration) m.calibration = [];
    m.calibration.push({
      predictedSigma: predicted.sigma,
      realisedAbsError: Math.abs((point.outputs[o.name] ?? 0) - truth),
    });
  }
}

// ---------------------------------------------------------------------------
// Main tick
// ---------------------------------------------------------------------------

function tickAll(): void {
  for (const state of campaignStore.all()) {
    if (state.status !== "running") continue;
    tickCampaign(state);
  }
}

function tickCampaign(state: CampaignState): void {
  const rt = getOrCreateRuntime(state.id);
  const runtimeRng = makeRng(state.id, `tick-${rt.seedPart++}`);

  const models = fitAllOutputs(
    state.config.inputs,
    state.config.outputs,
    completedObservations(state),
  );
  const range = rangeAxes(state.config.inputs);
  const events: WebSocketEvent[] = [];

  // Move queued points to running; initialise their sim timers.
  for (const p of state.points) {
    if (p.status === "queued") {
      p.status = "running";
      const targetSec = simulateRuntimeSec(state.id, p.id);
      rt.pointSim.set(p.id, { startMs: now(), targetDurationSec: targetSec });
      // Record surrogate prediction at the point so we have predicted sigma.
      if (range.length > 0) {
        const xn = normalisePoint(p.inputs, range);
        const pred: Record<string, { mean: number; sigma: number }> = {};
        for (const { name, model } of models) {
          pred[name] = predict(model, xn);
        }
        p.predicted = pred;
      }
      events.push({ type: "point", campaignId: state.id, point: { ...p } });
    }
  }

  // Advance running points. Complete those whose sim time has elapsed.
  for (const p of state.points) {
    if (p.status !== "running") continue;
    const sim = rt.pointSim.get(p.id);
    if (!sim) continue;
    const elapsedSec = (now() - sim.startMs) / 1000;
    // Scale sim time so mock campaigns complete quickly: compress 10-40 sim
    // seconds into ~1-4 wall seconds by default. Fast mode (used by the
    // Playwright acceptance suite) further compresses to ~0.1-0.4 wall s.
    const speed =
      import.meta.env.VITE_ADAPTIVE_SAMPLING_FAST === "true" ? 100 : 10;
    if (elapsedSec * speed >= sim.targetDurationSec) {
      p.status = "done";
      p.runtimeSec = sim.targetDurationSec;
      const perRun = state.config.flexCreditsPerHighFiRun ?? 1;
      p.costFlexCredits = Math.max(
        1,
        Math.min(2 * perRun, (sim.targetDurationSec * perRun) / 30),
      );

      // Outputs: oracle-derived if available, else synthetic from surrogate.
      const truth = truthForPoint(state, p);
      if (truth) {
        // Add a tiny noise around the truth to mimic "measured with noise".
        const noiseRng = makeRng(state.id, `noise-${p.id}`);
        const noisy: Record<string, number> = {};
        for (const [k, v] of Object.entries(truth)) {
          const jitter = (noiseRng() - 0.5) * 0.02 * (Math.abs(v) + 0.1);
          noisy[k] = v + jitter;
        }
        p.outputs = noisy;
        p.truthOutputs = truth;
      } else {
        p.outputs = syntheticOutputs(state, p, models, runtimeRng);
      }

      // Update autonomous/manual counters and cost.
      if (p.source === "manual") {
        state.metrics.manualRunCount += 1;
      } else {
        state.metrics.autonomousRunCount += 1;
      }
      state.metrics.cumulativeCostFlexCredits += p.costFlexCredits ?? 0;

      recordCalibration(state, p);
      rt.pointSim.delete(p.id);
      events.push({ type: "point", campaignId: state.id, point: { ...p } });
    }
  }

  // Recount in-flight / complete.
  state.pointsComplete = state.points.filter((p) => p.status === "done").length;
  state.pointsInFlight = state.points.filter(
    (p) => p.status === "queued" || p.status === "running",
  ).length;

  // If the current batch is complete, publish metrics and propose next batch.
  if (currentBatchComplete(state) && state.points.length > 0) {
    recomputeMetrics(state);
    pushBatchMetrics(state);
    events.push({ type: "metrics", campaignId: state.id, metrics: { ...state.metrics } });

    const budgetUsed = state.points.filter((p) => p.source === "sampler").length;
    const budget = state.config.budget ?? Infinity;
    const shouldStop = budgetUsed >= budget || targetsMet(state);

    if (shouldStop) {
      state.status = "done";
      events.push({ type: "completed", campaignId: state.id });
    } else if (!rt.nextBatchArmed) {
      rt.nextBatchArmed = true;
      const proposals = proposeBatch(
        state.id,
        state.config.inputs,
        state.config.outputs,
        completedObservations(state),
        state.config.batchSize,
        state.iteration + 1,
      );
      if (proposals.length > 0) {
        state.iteration += 1;
        const batchId = campaignStore.nextBatchId("sampler");
        const newIds: string[] = [];
        for (const inputs of proposals) {
          const pid = campaignStore.nextPointId();
          state.points.push({
            id: pid,
            iter: state.iteration,
            batchId,
            status: "queued",
            source: "sampler",
            inputs,
          });
          newIds.push(pid);
        }
        events.push({
          type: "batch",
          campaignId: state.id,
          iteration: state.iteration,
          pointIds: newIds,
        });
      }
      rt.nextBatchArmed = false;
    }
  }

  campaignStore.set(state);
  for (const ev of events) campaignStore.emit(state.id, ev);
}

// Used by the router after creating a campaign / resuming from paused.
export function kickCampaign(state: CampaignState): void {
  ensureTickLoopRunning();
  // If no points yet, seed with an initial sampler batch.
  if (state.points.length === 0) {
    const proposals = proposeBatch(
      state.id,
      state.config.inputs,
      state.config.outputs,
      [],
      state.config.batchSize,
      1,
    );
    if (proposals.length > 0) {
      state.iteration = 1;
      const batchId = campaignStore.nextBatchId("sampler");
      const newIds: string[] = [];
      for (const inputs of proposals) {
        const pid = campaignStore.nextPointId();
        state.points.push({
          id: pid,
          iter: 1,
          batchId,
          status: "queued",
          source: "sampler",
          inputs,
        });
        newIds.push(pid);
      }
      state.pointsInFlight = state.points.length;
      campaignStore.set(state);
      campaignStore.emit(state.id, { type: "state", campaignId: state.id, state });
      campaignStore.emit(state.id, {
        type: "batch",
        campaignId: state.id,
        iteration: 1,
        pointIds: newIds,
      });
    }
  }
}

export function subscribeMockStream(
  campaignId: string,
  onEvent: (ev: WebSocketEvent) => void,
): () => void {
  ensureTickLoopRunning();
  return campaignStore.subscribe(campaignId, onEvent);
}

// Exported for router use: public helpers for surface/score grids.
export { fitAllOutputs, recomputeMetrics };
